import React from 'react'
import { observer } from 'mobx-react'
import ConfirmSheet from 'components/ConfirmSheet'
import { DialogIllustration } from 'components/Dialog'
import {
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
} from 'components/ModalSheet'
import { TableHost } from 'components/Table'
import WorkspaceFilterViewHeading from 'components/WorkspaceFilterViewHeading'
import VirtualTable from 'payouts/components/VirtualTable'
import UserManagementEmptyState from 'user-management/components/UserManagementEmptyState'
import DocumentCount from '../DocumentManagementTable/DocumentCount'
import DocumentManagementFilterViewer from '../DocumentManagementTable/DocumentManagementFilterViewer'
import DocumentManagementTableHeaderRow from '../DocumentManagementTable/DocumentManagementTableHeaderRow'
import { DocumentManagementTableCell } from '../DocumentManagementTable/DocumentManagementTableRow'
import { DocumentManagementViewModel } from '../DocumentManagementTable/DocumentManagementViewModel'
import { DocumentsTabViewModel } from './DocumentsTabViewModel'
import styles from './DocumentsTab.sass'

interface DocumentsTabProps {
  vm: DocumentsTabViewModel
}

export default observer(({ vm }: DocumentsTabProps) => {
  return (
    <div className={styles.root}>
      <div className={styles.workspaceFilter}>
        <WorkspaceFilterViewHeading
          allOptionText="All Workspaces"
          primaryText=""
          filter={vm.documentManagementVM.searcher.workspacesFilter}
          viewHeadingSize="small"
        />
        <DocumentCount vm={vm.documentManagementVM} />
      </div>

      <div className={styles.documentsTable}>
        <TableHost>
          <DocumentManagementFilterViewer vm={vm.documentManagementVM} />

          {!vm.shouldShowEmptyState ? (
            <VirtualTable
              overscan={50}
              scroller={vm.documentManagementVM.searcher.scroller}
              totalCount={vm.documentManagementVM.searcher.documents.length}
              headerContent={() => (
                <DocumentManagementTableHeaderRow
                  vm={vm.documentManagementVM}
                  getSortOrder={(field) =>
                    vm.documentManagementVM.getSortOrder(field)
                  }
                  setSortOrder={(field, direction) =>
                    vm.documentManagementVM.setSortOrder(field, direction)
                  }
                />
              )}
              itemContent={(index) => {
                const document =
                  vm.documentManagementVM.searcher.documents[index]
                return (
                  <DocumentManagementTableCell
                    vm={vm.documentManagementVM}
                    document={document}
                  />
                )
              }}
            />
          ) : (
            <UserManagementEmptyState
              isSearchFilterApplied={vm.isSearchFilterApplied}
              onClearSearchFilterClick={() => vm.clearFilters()}
            />
          )}

          <ArchiveDocumentConfirm vm={vm.documentManagementVM} />
        </TableHost>
      </div>
    </div>
  )
})

interface ArchiveDocumentConfirmProps {
  vm: DocumentManagementViewModel
}

const ArchiveDocumentConfirm = observer(
  ({ vm }: ArchiveDocumentConfirmProps) => {
    const documentReference = vm.confirmDocumentArchive.bulkConfirm
      ? 'these documents'
      : 'this document'
    return (
      <ConfirmSheet
        yes="Delete Forever"
        danger
        state={vm.confirmDocumentArchive}
      >
        <ModalSheetContentBlock>
          <DialogIllustration
            src={require('assets/images/img_remove-collaborator.svg')}
            alt="Illustration of a women holding a phone with a crossed-out bubble above the phone."
          />
          <ModalSheetContentTitle>
            {`Are you sure you want to delete ${documentReference}?`}
          </ModalSheetContentTitle>
          <ModalSheetContentDescription>
            {`This action cannot be undone. Are you sure that you want to
            permanently delete ${documentReference}?`}
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
      </ConfirmSheet>
    )
  },
)
